/**
 * 来場者向けドットゲーム風下部切替バー
 */

import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import React, { useMemo } from 'react';
import { Image, Pressable, Text, View } from '../../../shared/components/WebPrimitives';
import useCompactViewport from '../../../shared/hooks/useCompactViewport';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import { tourAnchor } from '../../../shared/components/guidedTourAnchor';
import HOME_ICON from '../../../../assets/ホーム/ホーム.png';
import REWARD_ICON from '../../../../assets/ホーム/交換.png';
import QR_ICON from '../../../../assets/ホーム/QR.png';
import SURVEY_ICON from '../../../../assets/ホーム/アンケート.png';

/** タブアイコン画像 */
const TAB_ICONS = {
  [VISITOR_ROUTES.HOME]: HOME_ICON,
  [VISITOR_ROUTES.REWARDS]: REWARD_ICON,
  [VISITOR_ROUTES.SCAN]: QR_ICON,
  [VISITOR_ROUTES.SURVEYS]: SURVEY_ICON,
};

/** 下部切替項目定義（anchorProps は使い方ガイドが「次に押すタブ」として囲む目印） */
export const VISITOR_BOTTOM_TAB_ITEMS = Object.freeze([
  { routeName: VISITOR_ROUTES.HOME, label: 'ホーム', anchorProps: tourAnchor('tab.home') },
  { routeName: VISITOR_ROUTES.REWARDS, label: '景品', anchorProps: tourAnchor('tab.rewards') },
  { routeName: VISITOR_ROUTES.SCAN, label: 'QRスキャン', anchorProps: tourAnchor('tab.scan') },
  { routeName: VISITOR_ROUTES.SURVEYS, label: 'アンケート', anchorProps: tourAnchor('tab.surveys') },
]);

/**
 * 表示の段階ごとの下部切替バーの寸法。
 * 幅320px・文字「とても大きい」でも「QRスキャン」「アンケート」（5文字分）を1行に収めるため、圧縮表示では余白と字間を詰める
 */
const BOTTOM_BAR_LAYOUTS = Object.freeze({
  regular: {
    bar: 'px-2 py-2',
    row: 'gap-2',
    tab: 'min-h-[56px] flex-col px-1 py-2',
    iconSize: 28,
    label: 'mt-0.5 text-[11px] tracking-normal',
  },
  compact: {
    bar: 'px-0.5 py-1',
    row: 'gap-0.5',
    tab: 'min-h-[48px] flex-col px-0 py-1',
    iconSize: 23,
    label: 'mt-0.5 text-[10px] tracking-tighter',
  },
  // 横向きのスマートフォンは高さが足りないため、アイコンと名前を横に並べて本文の高さを残す
  short: {
    bar: 'px-1 py-0.5',
    row: 'gap-1',
    tab: 'min-h-[44px] flex-row gap-1.5 px-1 py-0.5',
    iconSize: 20,
    label: 'text-[11px]',
  },
});

/**
 * 表示中ルートからアクティブタブを解決する
 * @param {string} activeRouteName - 表示中ルート名
 * @returns {string} アクティブルート名
 */
export const resolveActiveTabRouteName = (activeRouteName) => {
  // イベント一覧は下部タブから外したため、ホームに寄せる
  if (activeRouteName === VISITOR_ROUTES.EVENTS) {
    return VISITOR_ROUTES.HOME;
  }

  if (
    activeRouteName === VISITOR_ROUTES.REDEEM_QR
    || activeRouteName === VISITOR_ROUTES.REDEMPTIONS
  ) {
    return VISITOR_ROUTES.REWARDS;
  }

  return activeRouteName;
};

/**
 * 来場者向けドットゲーム風下部切替バー
 * @param {Object} props - プロパティ
 * @param {Object | undefined} props.navigation - ナビゲーション
 * @param {string} props.activeRouteName - 表示中ルート
 * @returns {JSX.Element | null} 下部切替バー
 */
const VisitorBottomSwitchBar = ({ navigation, activeRouteName }) => {
  const { settings } = usePublicAppSettings();
  /** 表示の段階 */
  const { isNarrow, isShort } = useCompactViewport();
  /** 表示の段階に応じた寸法（字間を詰めるのは幅が狭いときだけ。高さだけ低いタブレットは通常の寸法のまま） */
  const layout = BOTTOM_BAR_LAYOUTS[isShort ? 'short' : isNarrow ? 'compact' : 'regular'];
  const activeTabRouteName = useMemo(() => {
    return resolveActiveTabRouteName(activeRouteName);
  }, [activeRouteName]);

  if (!navigation || typeof navigation.navigate !== 'function') {
    return null;
  }

  return (
    <View
      aria-label="主要メニュー"
      className={`rounded-none border-4 border-app-quest-night bg-[#FFF4C2] shadow-[0_-6px_0_rgba(26,45,77,0.35)] ${layout.bar}`}
      role="navigation"
      {...tourAnchor('tabBar')}
    >
      <View className={`flex-row items-center justify-between ${layout.row}`}>
        {VISITOR_BOTTOM_TAB_ITEMS.map((tabItem) => {
          const isPreparing = (tabItem.routeName === VISITOR_ROUTES.REWARDS && settings.homeFeatureVisibility.rewardsPreparing)
            || (tabItem.routeName === VISITOR_ROUTES.SURVEYS && settings.homeFeatureVisibility.surveysPreparing);
          const isActive = tabItem.routeName === activeTabRouteName;
          return (
            <Pressable
              key={tabItem.routeName}
              accessibilityLabel={tabItem.label}
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
              aria-current={isActive ? 'page' : undefined}
              className={`${layout.tab} flex-1 items-center justify-center rounded-none ${
                isActive
                  ? 'border-2 border-app-quest-gold bg-app-quest-night'
                  : 'border-2 border-transparent bg-transparent'
              }`}
              onPress={() => navigation.navigate(tabItem.routeName)}
              {...tabItem.anchorProps}
            >
              <Image
                accessible={false}
                resizeMode="contain"
                source={TAB_ICONS[tabItem.routeName]}
                style={{ height: layout.iconSize, width: layout.iconSize }}
              />
              <Text className={`font-extrabold ${layout.label} ${isActive ? 'text-white' : 'text-app-text'}`}>
                {/* 折り返すときは名前と「（準備中）」の境目だけで折る */}
                <Text className="whitespace-nowrap">{tabItem.label}</Text>
                {isPreparing ? <Text className="whitespace-nowrap">（準備中）</Text> : null}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

export default VisitorBottomSwitchBar;
